import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';

function money(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(x);
}

function skuKey(row) {
  return `${row.product_id}-${row.variation_id ?? 0}`;
}

function productLabel(row) {
  if (!row) return '';
  const base = row.product_name || `Product #${row.product_id}`;
  if (row.variation_id) return `${base} · ${row.variation_name || `Variation #${row.variation_id}`}`;
  return base;
}

export default function KotilingaSponsorsByProductPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [products, setProducts] = React.useState([]);
  const [productFilter, setProductFilter] = React.useState(null);
  const [sponsorQ, setSponsorQ] = React.useState('');
  const [sponsorQDebounced, setSponsorQDebounced] = React.useState('');
  const [sponsors, setSponsors] = React.useState([]);
  const [sponsorsLoading, setSponsorsLoading] = React.useState(false);
  const [sponsorPage, setSponsorPage] = React.useState(1);
  const [sponsorsMeta, setSponsorsMeta] = React.useState({
    total_count: 0,
    total_pages: 1,
  });
  const sponsorsAbortRef = React.useRef(null);

  React.useEffect(() => {
    const t = setTimeout(() => setSponsorQDebounced(sponsorQ), 350);
    return () => clearTimeout(t);
  }, [sponsorQ]);

  React.useEffect(() => {
    let c = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await get('/ldb/kotilinga-sponsors-by-product/products');
        if (!c) setProducts(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (!c) setErr(e?.message || 'Failed to load products');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, []);

  const loadSponsors = React.useCallback(async () => {
    if (!productFilter) {
      setSponsors([]);
      setSponsorsMeta({ total_count: 0, total_pages: 1 });
      return;
    }
    try {
      sponsorsAbortRef.current?.abort();
    } catch (_) {}
    const ac = new AbortController();
    sponsorsAbortRef.current = ac;
    const params = {
      product_id: productFilter.product_id,
      variation_id: productFilter.variation_id ?? 0,
      page: sponsorPage,
      page_size: 50,
    };
    if (sponsorQDebounced) params.q = sponsorQDebounced;
    setSponsorsLoading(true);
    try {
      const res = await get('/ldb/kotilinga-sponsors-by-product/sponsors', params, { signal: ac.signal });
      if (ac.signal.aborted) return;
      setSponsors(Array.isArray(res?.data) ? res.data : []);
      const m = res?.meta || {};
      setSponsorsMeta({
        total_count: Number(m.total_count || 0),
        total_pages: Math.max(1, Number(m.total_pages || 1)),
      });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setErr(e?.message || 'Failed to load sponsors');
    } finally {
      if (!ac.signal.aborted) setSponsorsLoading(false);
    }
  }, [productFilter, sponsorPage, sponsorQDebounced]);

  React.useEffect(() => {
    loadSponsors();
  }, [loadSponsors]);

  const productOptions = React.useMemo(
    () => products.map((p) => ({ ...p, key: skuKey(p), label: productLabel(p) })),
    [products]
  );

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <IconButton onClick={() => navigate('/kotilinga/sponsorships')} size="small" title="Kotilinga dashboard">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Sponsors by product</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            One row per catalog SKU. Select a product below to browse sponsors (completed + pledge statuses).
          </Typography>
        </Box>
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>
          {err}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, mb: 3 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Variation</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Unique sponsors</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount (paid)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((r) => (
                <TableRow
                  key={skuKey(r)}
                  hover
                  selected={productFilter != null && skuKey(productFilter) === skuKey(r)}
                  onClick={() => {
                    setProductFilter(r);
                    setSponsorPage(1);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell>{r.variation_id ? r.variation_name : '—'}</TableCell>
                  <TableCell align="right">{Number(r.unique_buyers || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{money(r.revenue_completed)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Sponsor list
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 2fr) minmax(220px, 1fr)' },
            mb: 2,
          }}
        >
          <Autocomplete
            size="small"
            options={productOptions}
            value={
              productFilter
                ? productOptions.find((o) => o.key === skuKey(productFilter)) || null
                : null
            }
            onChange={(_, v) => {
              setProductFilter(v || null);
              setSponsorPage(1);
            }}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.key === b?.key}
            renderInput={(params) => (
              <TextField {...params} label="Product" placeholder="Select product / variation" />
            )}
          />
          <TextField
            size="small"
            label="Sponsor name"
            value={sponsorQ}
            onChange={(e) => {
              setSponsorQ(e.target.value);
              setSponsorPage(1);
            }}
            placeholder="Search name…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {!productFilter ? (
          <Typography variant="body2" color="text.secondary">
            Select a product to view sponsors.
          </Typography>
        ) : sponsorsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {productLabel(productFilter)} · {sponsorsMeta.total_count.toLocaleString()} sponsors
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Orders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sponsors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No sponsors match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sponsors.map((s) => (
                      <TableRow key={s.contact_id ?? s.sponsor_name}>
                        <TableCell>{s.sponsor_name || '—'}</TableCell>
                        <TableCell>{s.customer_email || '—'}</TableCell>
                        <TableCell align="right">{money(s.total_paid)}</TableCell>
                        <TableCell align="right">{Number(s.orders_count || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {sponsorsMeta.total_pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={sponsorsMeta.total_pages}
                  page={sponsorPage}
                  onChange={(_, p) => setSponsorPage(p)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </PageLayout>
  );
}
